import { PageBuilderWidgetModel } from '@de-trustleap/shared-models';
import { processBackgroundColor } from '../utils/processBackgroundColor';

export abstract class BaseWidget<T> {
    protected properties: T;
    protected container: HTMLElement;
    protected disableExternalSpacing: boolean = false;

    constructor(properties: T) {
        this.properties = properties;
        this.container = document.createElement('div');
        this.container.classList.add('widget');
    }

    abstract render(): Promise<HTMLElement>;

    protected ensureId(element: HTMLElement, prefix: string): string {
        if (!element.id) {
            element.id = `${prefix}-${crypto.randomUUID().slice(0, 8)}`;
        }
        return element.id;
    }

    protected applyStyles(element: HTMLElement, styles: Partial<CSSStyleDeclaration>): void {
        Object.assign(element.style, styles);
    }

    protected formatColor(color?: string, fallback: string = '#000000'): string {
        if (!color) return fallback;
        if (color.startsWith('#')) return color;
        if (color.length === 8) {
            const alpha = parseInt(color.slice(0, 2), 16) / 255;
            const r = parseInt(color.slice(2, 4), 16);
            const g = parseInt(color.slice(4, 6), 16);
            const b = parseInt(color.slice(6, 8), 16);
            return `rgba(${r}, ${g}, ${b}, ${alpha})`;
        }
        return `#${color.slice(2)}`;
    }

    protected applySpacing(
        element: HTMLElement,
        padding?: { top?: number; right?: number; bottom?: number; left?: number },
        margin?: { top?: number; right?: number; bottom?: number; left?: number }
    ) {
        if (padding) {
            element.style.paddingTop = `${padding.top ?? 0}px`;
            element.style.paddingRight = `${padding.right ?? 0}px`;
            element.style.paddingBottom = `${padding.bottom ?? 0}px`;
            element.style.paddingLeft = `${padding.left ?? 0}px`;
        }

        if (margin) {
            element.style.marginTop = `${margin.top ?? 0}px`;
            element.style.marginRight = `${margin.right ?? 0}px`;
            element.style.marginBottom = `${margin.bottom ?? 0}px`;
            element.style.marginLeft = `${margin.left ?? 0}px`;
        }
    }

    protected applyShadow(
        element: HTMLElement,
        shadow?: {
            offset?: { x?: number; y?: number };
            blurRadius?: number;
            spreadRadius?: number;
            color?: string;
        }
    ) {
        if (shadow?.offset) {
            const x = shadow.offset.x ?? 0;
            const y = shadow.offset.y ?? 0;
            const blur = shadow.blurRadius ?? 0;
            const spread = shadow.spreadRadius ?? 0;
            const color = processBackgroundColor(shadow.color ?? 'FF000000');
            element.style.boxShadow = `${x}px ${y}px ${blur}px ${spread}px ${color}`;
        }
    }

    protected applyBackground(
        element: HTMLElement,
        background?: {
            backgroundColor?: string;
            imageProperties?: {
                url?: string;
                contentMode?: 'cover' | 'contain' | 'fill';
            };
            overlayColor?: string;
        }
    ) {
        if (!background) return;

        const { backgroundColor, imageProperties, overlayColor } = background;

        if (backgroundColor) {
            element.style.backgroundColor = processBackgroundColor(backgroundColor);
        }

        if (imageProperties?.url) {
            Object.assign(element.style, {
                backgroundImage: `url('${imageProperties.url}')`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center',
                backgroundSize:
                    imageProperties.contentMode === 'fill'
                        ? '100% 100%'
                        : imageProperties.contentMode === 'contain'
                            ? 'contain'
                            : 'cover',
                ...(overlayColor ? { backgroundBlendMode: 'overlay' } : {})
            });
        }

        if (overlayColor) {
            const overlay = document.createElement('div');
            overlay.classList.add('background-overlay');

            Object.assign(overlay.style, {
                position: 'absolute',
                inset: '0',
                zIndex: '-1',
                pointerEvents: 'none',
                backgroundColor: processBackgroundColor(overlayColor)
            });

            // Element muss relative sein, um Overlay zu halten
            if (getComputedStyle(element).position === 'static') {
                element.style.position = 'relative';
            }

            element.appendChild(overlay);
        }
    }

    public async getWidget(): Promise<HTMLElement> {
        const model = (this as any).widget as PageBuilderWidgetModel;

        // ✅ Stacking-Kontext: Overlay = -1, Container = 1
        this.applyStyles(this.container, {
            position: 'relative',
            zIndex: '1'
        });

        // 🎨 Hintergrund
        const background = (this.properties as any)?.background;
        this.applyBackground(this.container, background);

        // Inhalt einfügen
        const content = await this.render();
        this.container.appendChild(content);

        // ↔️ Spacing
        if (!this.disableExternalSpacing && (model?.padding || model?.margin)) {
            this.applySpacing(this.container, model.padding, model.margin);
        }

        // 🕶 Schatten
        const shadow = (this.properties as any)?.shadow;
        if (shadow) {
            this.applyShadow(this.container, shadow);
        }

        return this.container;
    }
}
